/**
 * AutomationValue
 *
 * A platform-independent value used in automation parameters and conditions.
 * Does not reference Blockly or Home Assistant.
 *
 * Kinds:
 *   'literal'   — a plain static string or number
 *   'template'  — an expression template, e.g. "{{ val }}"
 *   'reference' — a named runtime variable, e.g. "val", "checked", "text"
 *   'list'      — an ordered array of literal strings (e.g. multiple entity IDs)
 *   'entity'    — the state, or one attribute, of an external entity; its
 *                 entityId may be filled in later (e.g. from a flow-graph wire)
 */
export class AutomationValue {
  /**
   * @param {object} config
   * @param {'literal'|'template'|'reference'|'list'|'object'|'entity'} [config.kind]
   * @param {string}   [config.value]    — for kind='literal'
   * @param {string}   [config.template] — for kind='template'
   * @param {string}   [config.name]     — for kind='reference'
   * @param {string[]} [config.items]    — for kind='list'
   * @param {object}   [config.object]  — for kind='object'
   * @param {string}   [config.entityId]  — for kind='entity'
   * @param {string}   [config.attribute] — for kind='entity'; '' reads the state
   */
  constructor({ kind = 'literal', value = '', template, name, items, entityId, attribute } = {}) {
    this.kind = kind;
    switch (kind) {
      case 'template':  this.template = template ?? value; break;
      case 'reference': this.name = name ?? ''; break;
      case 'list':      this.items = items ?? []; break;
      case 'object':    this.value = value ?? {}; break;
      case 'entity':    this.entityId = entityId ?? ''; this.attribute = attribute ?? ''; break;
      default:          this.value = value; // 'literal'
    }
  }

  /**
   * Return the value as a string suitable for embedding in generated output.
   * Generators that need structured access (e.g. list items) should inspect
   * `.kind` directly rather than relying on toString().
   */
  toString() {
    switch (this.kind) {
      case 'template':  return this.template;
      case 'reference': return this.name;
      case 'list':      return this.items.join(', ');
      case 'object':    return JSON.stringify(this.value);
      case 'entity':    return this.attribute ? `${this.entityId}.${this.attribute}` : this.entityId;
      default:          return this.value;
    }
  }

  toJSON() {
    switch (this.kind) {
      case 'template':  return { kind: 'template',  template: this.template };
      case 'reference': return { kind: 'reference', name: this.name };
      case 'list':      return { kind: 'list',      items: this.items };
      case 'object':    return { kind: 'object',    value: this.value };
      case 'entity':    return { kind: 'entity',    entityId: this.entityId, attribute: this.attribute };
      default:          return { kind: 'literal',   value: this.value };
    }
  }

  static fromJSON(data) {
    if (!data) return null;
    return new AutomationValue(data);
  }

  /** Create a literal value. */
  static literal(value) {
    return new AutomationValue({ kind: 'literal', value: String(value) });
  }

  /** Create a template value (e.g. a Jinja2 expression). */
  static template(tpl) {
    return new AutomationValue({ kind: 'template', template: tpl });
  }

  /** Create a reference to a named runtime variable (e.g. 'val', 'checked'). */
  static reference(name) {
    return new AutomationValue({ kind: 'reference', name });
  }

  /** Create a list of literal strings (e.g. multiple entity IDs). */
  static list(items) {
    return new AutomationValue({ kind: 'list', items: [...items] });
  }

  /** Create a structured object value for an HA object selector. */
  static object(value) {
    return new AutomationValue({ kind: 'object', value });
  }

  /** Create a read of an entity's state, or of one of its attributes. */
  static entity(entityId = '', attribute = '') {
    return new AutomationValue({ kind: 'entity', entityId, attribute });
  }
}
